"use client";

import { useTranslations } from "next-intl";
import { Control, useFormContext } from "react-hook-form";

import ImageUploader from "@/components/common/forms/ImageUploader";
import backImage from "@/public/assets/images/help/back-small.jpg";
import driverSide from "@/public/assets/images/help/driver-side-small.jpg";
import engineBayImage from "@/public/assets/images/help/engine-bay-small.jpg";
import frontImage from "@/public/assets/images/help/front-small.jpg";
import gccSticker from "@/public/assets/images/help/gcc-sticker-small.jpg";
import interiorImage from "@/public/assets/images/help/interior-small.jpg";
import mileageImage from "@/public/assets/images/help/mileage-small.jpg";
import passengerSide from "@/public/assets/images/help/passenger-small.jpg";
import rtaPassingCertificate from "@/public/assets/images/help/rta-cert.png";

interface BankValuationCompleteFormSchemaType {
  specs: string;
}

interface ThirdStepSchemaType {
  images: any[];
}

interface BankValuationImagesStepProps {
  control: Control<ThirdStepSchemaType>;
}

const BankValuationImagesStep = ({ control }: BankValuationImagesStepProps) => {
  const { watch } = useFormContext<BankValuationCompleteFormSchemaType>();
  const {
    formState: { isSubmitting },
  } = useFormContext<ThirdStepSchemaType>();
  const t = useTranslations("BankValuation.imagesStep");

  const specs = watch("specs");
  const isGcc = (specs ?? "").toLowerCase().trim().includes("gcc");
  const numberOfImages = isGcc ? 9 : 8;

  // Build image guide data with translations
  const imageGuide = [
    {
      label: t("imageLabels.front"),
      description: t("imageDescriptions.front"),
      placeholder: frontImage,
    },
    {
      label: t("imageLabels.back"),
      description: t("imageDescriptions.back"),
      placeholder: backImage,
    },
    {
      label: t("imageLabels.driverSide"),
      description: t("imageDescriptions.driverSide"),
      placeholder: driverSide,
    },
    {
      label: t("imageLabels.passengerSide"),
      description: t("imageDescriptions.passengerSide"),
      placeholder: passengerSide,
    },
    {
      label: t("imageLabels.interior"),
      description: t("imageDescriptions.interior"),
      placeholder: interiorImage,
    },
    {
      label: t("imageLabels.mileagePhoto"),
      description: t("imageDescriptions.mileagePhoto"),
      placeholder: mileageImage,
    },
    {
      label: t("imageLabels.engineBay"),
      description: t("imageDescriptions.engineBay"),
      placeholder: engineBayImage,
    },
    {
      label: t("imageLabels.rtaCertificate"),
      description: t("imageDescriptions.rtaCertificate"),
      placeholder: rtaPassingCertificate,
    },
    {
      label: t("imageLabels.gccSticker"),
      description: t("imageDescriptions.gccSticker"),
      placeholder: gccSticker,
    },
  ];

  const labels = [
    t("imageLabels.front"),
    t("imageLabels.back"),
    t("imageLabels.driverSide"),
    t("imageLabels.passengerSide"),
    t("imageLabels.interior"),
    t("imageLabels.mileagePhoto"),
    t("imageLabels.engineBay"),
    t("imageLabels.rtaCertificate"),
  ];

  const allowedFileTypes: Array<"image" | "pdf" | "both"> = Array.from({ length: 7 }, () => "image");
  allowedFileTypes.push("pdf"); //! RTA Passing Certificate is a PDF

  if (isGcc) {
    labels.push(t("imageLabels.gccSticker"));
    allowedFileTypes.push("image");
  }

  // Build guidelines array matching the slots order
  const fullGuidelines = imageGuide.map((g) => ({ title: g.label, tip: g.description, img: g.placeholder }));
  const guidelines = isGcc ? fullGuidelines : fullGuidelines.slice(0, -1);

  return (
    <div className="mt-10">
      <div className="flex flex-col items-start sxs:flex-row sxs:items-center sxs:justify-between">
        <h3 className="font-inter text-base font-bold text-primary-foreground">{t("title")}</h3>
      </div>

      <div className="mt-4">
        <ImageUploader
          name="images"
          control={control}
          required
          maxImages={numberOfImages}
          labels={labels}
          validateImageCount={isSubmitting}
          maxSizeMB={25}
          enableCompression
          compressionQuality={60}
          compressionFormat="jpeg"
          guidelines={guidelines}
          guidelineOptions={{
            enabled: true,
            showGhostExample: false,
            showExampleButton: true,
            landscapeBadge: {
              title: t("mobileLandscapeBadge.title"),
              emphasis: t("mobileLandscapeBadge.emphasis"),
              note: t("mobileLandscapeBadge.note"),
            },
          }}
          allowedFileTypes={allowedFileTypes}
          s3FolderName="bank_valuation_images"
        />
      </div>
    </div>
  );
};

export default BankValuationImagesStep;
